import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import { instance } from '@/shared/api';
import { isPlatformHost } from '@/shared/lib';

// Инициализируем Pusher глобально только при загрузке WebSocket-сегмента.
if (typeof window !== 'undefined') {
  window.Pusher = Pusher;
}

/**
 * Данные авторизации приватного канала, которые возвращает Laravel /broadcasting/auth.
 */
interface ChannelAuthData {
  auth: string;
  channel_data?: string;
}

/**
 * Singleton Echo instance для WebSocket соединений.
 */
class EchoClient {
  private static instance: Echo<'reverb'> | null = null;

  static getInstance(): Echo<'reverb'> {
    if (isPlatformHost()) {
      throw new Error('Realtime is disabled on the platform host');
    }

    if (!EchoClient.instance) {
      EchoClient.instance = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT ?? 80,
        wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        authorizer: (channel: { name: string }) => ({
          authorize: (socketId: string, callback: (error: Error | null, data: ChannelAuthData | null) => void) => {
            instance
              .post<ChannelAuthData>('/broadcasting/auth', {
                socket_id: socketId,
                channel_name: channel.name,
              })
              .then((response) => {
                callback(null, response.data);
              })
              .catch((error: unknown) => {
                console.warn('[EchoClient] Ошибка авторизации приватного канала', channel.name, error);
                callback(error instanceof Error ? error : new Error('Broadcast auth failed'), null);
              });
          },
        }),
      });
    }

    return EchoClient.instance;
  }

  static disconnect(): void {
    if (EchoClient.instance) {
      EchoClient.instance.disconnect();
      EchoClient.instance = null;
    }
  }

  static channel(channelName: string) {
    return EchoClient.getInstance().channel(channelName);
  }

  static private(channelName: string) {
    return EchoClient.getInstance().private(channelName);
  }

  static join(channelName: string) {
    return EchoClient.getInstance().join(channelName);
  }

  static leave(channelName: string): void {
    EchoClient.getInstance().leave(channelName);
  }

  static isConnected(): boolean {
    return EchoClient.instance !== null;
  }
}

export default EchoClient;
